import React, { useState, useEffect, useRef } from 'react'
import dynamic from 'next/dynamic'
import LazyLoad from 'react-lazyload'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useArticleLayout, useCatalogs } from 'utils/api/hooks'
import { useCatalogsServiceMenus } from 'utils/api/hooks/useCatalogsServiceMenus'
import Link from 'components/common/A'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import ContentWrapper from 'components/common/ContentWrapper'
import { selectSelfConstruct } from 'store/selfConstruct/selfConstructSlice'
import ServiceLayout from './ServiceLayout'
import BannerCarousel from './BannerCarousel'
import SearchBarSection from 'components/pages/Index/SearchBarSection'
import PopularServiceLayout from './PopularServiceLayout'
import ShowAllServiceModal from 'components/common/ShowAllServiceModal'
import ScrollableWrapper from 'components/common/ScrollableWrapper'
import HomeServiceMenus from './HomeServiceMenus'
import Present from 'components/common/Present'
import { getLayoutModuleById } from 'utils/api'
import { GtmAgent } from 'utils/hooks/analytics'
import { useRouter } from 'next/router'
import { indexPageMaxContentWidth } from 'config/theme'
import PopularServices from './PopularServices'
import Footer from 'components/common/Footer'

const DynamicSyDomesticServiceList = dynamic(
  () => import('./SyDomesticServiceList'),
  { ssr: false }
)

const DynamicArticleLayoutSwiper = dynamic(
  () => import('./ArticleLayoutSwiper'),
  { ssr: false }
)

const emptySx = {}

const ClassificationTabs = styled(Tabs)`
  .MuiTabs-indicator {
    height: 5px;
  }

  .MuiTab-textColorInherit {
    opacity: 1;
  }
`

const CustomTab = styled(Tab)`
  padding: 30px 0;

  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      padding: 15px 30px;
    }
  `}
`

const TabButton = styled(Box)`
  ${({ theme }) => `
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    ${theme.breakpoints.up('lg')} {
      flex-direction: row;

      &:hover {
        background-color: ${theme.palette.primary.bgPlus1};
      }
    }
  `}
`

const TabHouseCleaning = React.forwardRef((props, ref) => {
  const selected = props['aria-selected']
  const [hovered, setHovered] = useState(false)
  return (
    <TabButton
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      sx={emptySx}
    >
      <img
        src={
          selected || hovered
            ? '/img/btn_menu_clean_selected.svg'
            : '/img/btn_menu_clean_inactive.svg'
        }
        width="64"
        height="46"
      />
      <Typography
        color="textPrimary"
        fontWeight={selected ? 'fontWeightBold' : 'fontWeightRegular'}
      >
        清潔消毒
      </Typography>
    </TabButton>
  )
})

const TabEmpty1 = React.forwardRef((props, ref) => {
  const selected = props['aria-selected']
  const [hovered, setHovered] = useState(false)
  return (
    <TabButton
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      sx={emptySx}
    >
      <img
        src={
          selected || hovered
            ? '/img/btn_menu_appliance_cleaning_selected.svg'
            : '/img/btn_menu_appliance_cleaning_inactive.svg'
        }
        width="64"
        height="46"
      />
      <Typography
        color="textPrimary"
        fontWeight={selected ? 'fontWeightBold' : 'fontWeightRegular'}
      >
        家電清潔
      </Typography>
    </TabButton>
  )
})

const TabEmpty2 = React.forwardRef((props, ref) => {
  const selected = props['aria-selected']
  const [hovered, setHovered] = useState(false)
  return (
    <TabButton
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      sx={emptySx}
    >
      <img
        src={
          selected || hovered
            ? '/img/btn_menu_wrench_selected.svg'
            : '/img/btn_menu_wrench_inactive.svg'
        }
        width="64"
        height="46"
      />
      <Typography
        color="textPrimary"
        fontWeight={selected ? 'fontWeightBold' : 'fontWeightRegular'}
      >
        裝潢修繕
      </Typography>
    </TabButton>
  )
})

const TabRelocation = React.forwardRef((props, ref) => {
  const selected = props['aria-selected']
  const [hovered, setHovered] = useState(false)
  return (
    <TabButton
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      ref={ref}
      sx={emptySx}
    >
      <img
        src={
          selected || hovered
            ? '/img/btn_menu_movehouse_selected.svg'
            : '/img/btn_menu_movehouse_inactive.svg'
        }
        width="64"
        height="46"
      />
      <Typography
        color="textPrimary"
        fontWeight={selected ? 'fontWeightBold' : 'fontWeightRegular'}
      >
        搬家回收
      </Typography>
    </TabButton>
  )
})

const PanelWrapper = styled(ContentWrapper)`
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      width: calc(100% - 76px);
      border: 1px solid ${theme.palette.background.gray};
      box-shadow: 0px 0px 8px #F4F4F4, 0px 2px 4px #FBFBFB;
    }

    ${theme.breakpoints.up('xl')} {
      width: calc(100% - 96px);
    }

    ${theme.breakpoints.up('xxl')} {
      width: calc(100% - 128px);
    }
  `}
`
const TabPanel = ({ index, value, children, ...rest }) => {
  if (index !== value) {
    return null
  }
  return (
    <PanelWrapper py={{ xs: 10, lg: 12.5 }} px={{ xs: 4, lg: 24 }} sx={emptySx}>
      {children}
    </PanelWrapper>
  )
}

const Index = ({ isMobile, fallbackData }) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const [tabIndex, setTabIndex] = useState(0)
  const onTabChange = (event, newTab) => setTabIndex(newTab)
  const selfConstruct = useSelector(selectSelfConstruct)
  const [layoutModules, setLayoutModules] = useState([])
  const { homePageLayout } = fallbackData
  const scrollableRef = useRef(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState(null)
  const { data: catalogsServiceMenus } = useCatalogsServiceMenus({
    target: 'mainHomeServiceMenu',
  })
  const router = useRouter()

  const handleTabChange = (tabData, isUserTriggered) => {
    setSelectedTab(tabData)
    // 只有使用者想切換而點擊時才送GA
    if (isUserTriggered) {
      GtmAgent.directSendToGTM({
        event: 'living_action',
        living_event_name: 'servicemenu_click',
        living_click_name: tabData?.label,
        living_click_id: tabData?._id,
      })
    }
  }

  const handleSubMenusLinkClick = () => {
    if (selectedTab?.link) {
      if (selectedTab?.linkTarget === 'blank') {
        window.open(selectedTab.link, 'blank')
      } else {
        router.push(selectedTab.link)
      }
    }
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'servicemenu_click',
      living_click_name:
        selectedTab?.linkDisplayName || `前往${selectedTab?.label}`,
      living_category1_name: selectedTab?.label,
      living_category1_id: selectedTab?._id,
    })
  }

  useEffect(() => {
    const hash = location.href.split('#')?.[1]
    const hashIndexMap = {
      cleaning: 0,
      electricEquipClean: 1,
      moving: 2,
      homedeco: 3,
    }
    // do nothing if hash index is 0, cause it's already is 0
    if (hashIndexMap[hash]) {
      setTabIndex(hashIndexMap[hash])
    }
  }, [])

  useEffect(() => {
    const getLayoutModules = async () => {
      const modules = homePageLayout?.modules
      const results = await Promise.all(
        modules.map((id) => getLayoutModuleById(id))
      )
      setLayoutModules(
        results.map(([data, error]) => {
          return error ? null : data
        })
      )
    }
    if (homePageLayout?.modules?.length) getLayoutModules()
  }, [homePageLayout?.modules])

  const [tabsVariant] = useState(isMobile ? 'fullWidth' : 'standard')

  const { data: layout } = useArticleLayout(
    'homepages',
    fallbackData?.articleLayout
      ? { fallbackData: [fallbackData.articleLayout] }
      : undefined
  )

  const { data: catalogsBelowBanner } = useCatalogs({
    isDisplayBelowBannerInfo: true,
    isSelfBookingSource: false,
    isGetBelongsSubCatalogs: true,
    isVisible: true,
  })

  const containerId = 'homepage-scrollable'

  return (
    <Layout
      stickyHeader={<Header />}
      mx={{ xs: 0, lg: 16 }}
      fixContentHeight={true}
    >
      <ShowAllServiceModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        catalogsData={catalogsBelowBanner}
      />
      <ScrollableWrapper wrapperRef={scrollableRef} containerId={containerId}>
        <SearchBarSection />
        <BannerCarousel
          fallbackData={fallbackData?.banners}
          isMobile={isMobile}
        />
        <HomeServiceMenus
          catalogsServiceMenus={catalogsServiceMenus}
          isSmall={isSmall}
          isMobile={isMobile}
          handleTabChange={handleTabChange}
          handleSubMenusLinkClick={handleSubMenusLinkClick}
        />

        {/* 目前以自訂模組實作，先註解掉 */}
        {/* <>
          <ServiceLayout
            openAllServiceModal={() => setIsModalOpen(true)}
            containerId={containerId}
            catalogsData={catalogsBelowBanner}
          />
          <PopularServiceLayout />
        </> */}
        {layoutModules?.map((module, index) => {
          return !module?.isVisible ? null : (
            <Box
              mb={
                module?.type === 'graphicMore' ||
                module?.type === 'publicitySmall' ||
                module?.type === 'banner'
                  ? { xs: 12, md: 18 }
                  : { xs: 18, md: 24 }
              }
              key={index}
            >
              <Present
                key={module?._id}
                data={module}
                lazy={true}
                lazyOptions={{ containerId }}
              />
            </Box>
          )
        })}
        {layout?.homepages?.map((articleLayout, index) => (
          <LazyLoad
            height={480}
            offset={50}
            key={`layout-${index}`}
            scrollContainer={`#${containerId}`}
          >
            <Box minHeight={345}>
              <DynamicArticleLayoutSwiper data={articleLayout} />
            </Box>
          </LazyLoad>
        ))}
        <PopularServices catalogsServiceMenus={catalogsServiceMenus} />
        <Box minHeight={436}>
          <Footer />
        </Box>
      </ScrollableWrapper>
    </Layout>
  )
}

export default Index
